import { useCallback, useEffect, useState } from "react"
import { GetApp } from "../../App"
import { ConfirmDialog } from "../../Components/ConfirmDialog"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { logger } from "../../App/Logger"
import { ApiClient, CruiseApi, ICreateStatus, ICruiseCreateResponse, TripApi } from "../../Types"
import { FocusViewConfirmationType } from "./Context"
import { FocusConfirmationDialogViewProps } from "./Params"
import { useFocusData } from "./DataContext"

export const FocusViewConfirmationDialogData = {
  canCreate(api: ApiClient, confirmationType: FocusViewConfirmationType): Promise<ICruiseCreateResponse> {
    if (confirmationType === FocusViewConfirmationType.cruise) {
      return new CruiseApi(api).cruises.canCreate({ params: {} }).call();
    }
    return new TripApi(api).trips.canCreate({ params: {} }).call();
  },
  create(api: ApiClient, confirmationType: FocusViewConfirmationType): Promise<ICruiseCreateResponse> {
    if (confirmationType === FocusViewConfirmationType.cruise) {
      return new CruiseApi(api).cruises.create({ params: {} }).call();
    }
    return new TripApi(api).trips.create({ params: {} }).call();
  },
};

export function FocusConfirmationDialogView({ complete, confirmationType }: FocusConfirmationDialogViewProps) {
  const app = GetApp()!;
  const t = app.getT("FocusView");
  const { writers } = useFocusData();
  const [status, setStatus] = useState<string | undefined>(undefined);

  useEffect(() => {
    const load = async () => {
      const result = await FocusViewConfirmationDialogData.canCreate(app.apiClient, confirmationType);
      logger.debug("start confirmation for", confirmationType, result?.status);
      if (result) {
        setStatus(result.status);
      }
    };
    load().catch(console.error);
  }, []);

  const action = useCallback(async () => {
    if (status != ICreateStatus.allowed) {
      return;
    }
    const result = await (confirmationType === FocusViewConfirmationType.cruise
      ? writers.createCruise()
      : writers.createTrip());
    logger.debug("create", confirmationType, result?.cruise?.id, result?.trip?.id);
    if (result && (result.cruise || result.trip)) {
      complete(undefined);
    }
  }, [status, confirmationType, writers]);

  return (
    <ConfirmDialog
      title={t(`confirm_${confirmationType}_title`)}
      style={status == ICreateStatus.allowed ? "default" : "warning"}
      loading={!status}
      confirmDisabled={status != ICreateStatus.allowed}
      message={<LocalizedMessage message={(m) => m.currentView.confirmationDialog[confirmationType][status!]} />}
      cancelLabel={t("close")}
      confirmLabel={t("confirm")}
      onCancel={() => complete(undefined)}
      onConfirm={action}
    />
  );
}
